import { Address } from '~api/address_book';
import type { Dimensions as DimensionType, DirectionType as SomDirectionType } from '~api/som';

import { ReactElement } from 'react';
import { ComponentProps, PropsWithChildren } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { Banner, Button, DotBadge, ListLayout, PileBadge, SidePanel, TableSidePanel } from '@ingrid/luterkort-react';
import { CarrierName, ShippingLogo } from '@ingrid/react-shipping-logo';

import { AddressMultiline } from '~src/components/AddressMultiline/AddressMultiline';
import { useCarrierTransportProduct } from '~src/components/CarrierTransportProduct/CarrierTransportProduct';
import { Dimensions } from '~src/components/Dimensions/Dimensions';
import { LocalizedDate } from '~src/i18n/localized-date';
import { useTranslation } from '~src/i18n/use-translation';
import { LocalizationKey } from '~src/i18n/use-translation';
import { DirectionType } from '~src/pages/shipment/details/direction-type';

import { Amount } from '../Amount/Amount';

interface DetailsDrawerProps {
  actions?: ReactElement;
  detailsHref?: string;
  nodes: JSX.Element[];
  title?: string;
}

interface NodeWithLabel {
  label: LocalizationKey;
}

type PlainNode = NodeWithLabel & {
  value: string | number | undefined;
};

type BadgeNode = NodeWithLabel & {
  value: string | undefined;
  variant?: 'neutral' | 'informative' | 'success' | 'warning' | 'critical';
};

type ArrayCommaNode = NodeWithLabel & {
  value: string[] | undefined;
};

type CarrierProductNode = NodeWithLabel & {
  value: string | undefined;
};

type CarrierNode = NodeWithLabel & {
  value: string | undefined;
};

type CartItemsNode = NodeWithLabel & {
  value:
    | {
        quantity?: string | number | undefined;
        name?: string | undefined;
      }[]
    | undefined;
};

type CurrencyNode = NodeWithLabel & {
  currency: string | undefined;
  value: number | undefined;
};

type AddressNode = NodeWithLabel & {
  value: Address | undefined;
};

type DateNode = NodeWithLabel & {
  value: string | undefined;
  time?: boolean;
};

type BannerNode = ComponentProps<typeof Banner> & {
  when: boolean | undefined;
};

type DotBadgeNode = NodeWithLabel & {
  variant: 'informative' | 'success' | 'warning' | 'critical' | 'neutral' | undefined;
  text: string | undefined;
};

type SubRowNode = NodeWithLabel & {
  value: string[] | undefined;
};

type DimensionNode = NodeWithLabel & {
  value: DimensionType | undefined;
};

type DirectionNode = NodeWithLabel & {
  value: SomDirectionType | undefined;
};

function Stack({ children }: PropsWithChildren) {
  return <ListLayout.Stack as="div">{children}</ListLayout.Stack>;
}

function LabelStack({ children, label }: PropsWithChildren<NodeWithLabel>) {
  const { t } = useTranslation();

  return (
    <Stack>
      <ListLayout.SecondaryText as="dt">{t(label)}</ListLayout.SecondaryText>
      {children}
    </Stack>
  );
}

function RenderPlainNode({ label, value }: PlainNode) {
  if (!value) {
    return false;
  }

  return (
    <LabelStack label={label}>
      <ListLayout.PrimaryText as="dd">{value}</ListLayout.PrimaryText>
    </LabelStack>
  );
}

function RenderBadgeNode({ label, value }: BadgeNode) {
  if (!value) {
    return false;
  }

  return (
    <LabelStack label={label}>
      <PileBadge as="dd" style={{ display: 'inline-flex', maxWidth: '100%' }} variant="neutral">
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</span>
      </PileBadge>
    </LabelStack>
  );
}

function RenderArrayCommaNode({ value, ...rest }: ArrayCommaNode) {
  if (!value?.filter(s => !!s).length) {
    return false;
  }

  return <RenderPlainNode value={value.join(', ')} {...rest} />;
}

function RenderCarrierNode({ label, value }: CarrierNode) {
  if (!value) {
    return false;
  }

  return (
    <LabelStack label={label}>
      <ListLayout.SubRow as="dd" gap="sm">
        <ShippingLogo method={value} width={20} />
        <ListLayout.PrimaryText>
          <CarrierName method={value} />
        </ListLayout.PrimaryText>
      </ListLayout.SubRow>
    </LabelStack>
  );
}

function RenderCarrierProductNode({ label, value }: CarrierProductNode) {
  const { method } = useCarrierTransportProduct({
    queryOptions: { customBookingMethods: false, onlyConfigured: false },
    methodId: value,
  });

  if (!value || !method?.id) {
    return false;
  }

  return (
    <LabelStack label={label}>
      <ListLayout.SubRow as="dd" gap="sm">
        <ShippingLogo method={method.id} width={20} />
        <ListLayout.PrimaryText>{method.name}</ListLayout.PrimaryText>
      </ListLayout.SubRow>
    </LabelStack>
  );
}

function RenderCartItemsNode({ label, value }: CartItemsNode) {
  if (!value?.length) {
    return false;
  }

  return (
    <LabelStack label={label}>
      {value.map(item => (
        <ListLayout.SubRow as="dd" key={`${item.name}+${item.quantity}`}>
          <ListLayout.PrimaryText>{`${item.quantity ?? 1}×`}</ListLayout.PrimaryText>
          <ListLayout.PrimaryText
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textTransform: 'capitalize',
              whiteSpace: 'nowrap',
            }}
          >
            {item.name}
          </ListLayout.PrimaryText>
        </ListLayout.SubRow>
      ))}
    </LabelStack>
  );
}

function RenderCurrencyNode({ value, currency, label }: CurrencyNode) {
  if (!value || !currency) {
    return false;
  }

  return (
    <LabelStack label={label}>
      <ListLayout.PrimaryText as="dd">
        <Amount value={value} currency={currency} />
      </ListLayout.PrimaryText>
    </LabelStack>
  );
}

function RenderAddressNode({ label, value }: AddressNode) {
  if (!value) {
    return false;
  }

  return (
    <LabelStack label={label}>
      <ListLayout.PrimaryText as="dd">
        <AddressMultiline address={value} />
      </ListLayout.PrimaryText>
    </LabelStack>
  );
}

function RenderDateNode({ label, value, time = false }: DateNode) {
  if (!value) {
    return false;
  }

  return (
    <LabelStack label={label}>
      <ListLayout.PrimaryText as="dd">
        <LocalizedDate dateTime={value} time={time} />
      </ListLayout.PrimaryText>
    </LabelStack>
  );
}

function RenderBannerNode({ title, variant, when, subtitle }: BannerNode) {
  if ((!title && !subtitle) || !when) {
    return false;
  }

  return (
    <Stack>
      <Banner title={title} variant={variant} subtitle={subtitle} />
    </Stack>
  );
}

function RenderDotBadgeNode({ label, text, variant }: DotBadgeNode) {
  if (!variant || !text) {
    return false;
  }

  return (
    <LabelStack label={label}>
      <ListLayout.PrimaryText as="dd" style={{ alignItems: 'center', display: 'flex' }}>
        <DotBadge variant={variant} />
        <span>{text}</span>
      </ListLayout.PrimaryText>
    </LabelStack>
  );
}

function RenderSubRowNode({ label, value }: SubRowNode) {
  if (!value?.filter(s => !!s).length) {
    return false;
  }

  return (
    <LabelStack label={label}>
      {value.map((val, index) => (
        <ListLayout.SubRow as="dd" key={`sub-row-${index}`}>
          <ListLayout.PrimaryText>{val}</ListLayout.PrimaryText>
        </ListLayout.SubRow>
      ))}
    </LabelStack>
  );
}

function RenderDimensionNode({ label, value }: DimensionNode) {
  if (!value?.height || !value.length || !value.width) {
    return false;
  }

  return (
    <LabelStack label={label}>
      <ListLayout.PrimaryText>
        <Dimensions dimensions={value} />
      </ListLayout.PrimaryText>
    </LabelStack>
  );
}

function RenderDirectionNode({ label, value }: DirectionNode) {
  if (!value) {
    return false;
  }

  return (
    <LabelStack label={label}>
      <DirectionType direction={value} as="dd" />
    </LabelStack>
  );
}

function DetailsDrawerElement({ nodes, detailsHref, actions, title = '' }: DetailsDrawerProps) {
  const { t } = useTranslation();

  return (
    <TableSidePanel>
      <SidePanel.Header title={title} action={actions} />

      <SidePanel.Body>
        <ListLayout.Column as="dl">{nodes.map(node => node)}</ListLayout.Column>
      </SidePanel.Body>

      {detailsHref && (
        <SidePanel.Footer>
          <Button as={RouterLink} to={detailsHref} variant="quaternary">
            {t('common.seeFullDetails')}
          </Button>
        </SidePanel.Footer>
      )}
    </TableSidePanel>
  );
}

export const DetailsDrawer = Object.assign(DetailsDrawerElement, {
  Plain: RenderPlainNode,
  Badge: RenderBadgeNode,
  Comma: RenderArrayCommaNode,
  Carrier: RenderCarrierNode,
  CarrierProduct: RenderCarrierProductNode,
  Order: RenderCartItemsNode,
  Currency: RenderCurrencyNode,
  Address: RenderAddressNode,
  Date: RenderDateNode,
  Banner: RenderBannerNode,
  DotBadge: RenderDotBadgeNode,
  SubRow: RenderSubRowNode,
  Dimension: RenderDimensionNode,
  Direction: RenderDirectionNode,
});
